import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, Radio } from 'lucide-react';
import AdSlot from '@/components/AdSlot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Get current date/time in WAT (UTC+1)
const getWATDate = () => {
  const now = new Date();
  return new Date(now.getTime() + (1 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
};

export default function SchedulePage() {
  const [schedule, setSchedule] = useState([]);
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = getWATDate().getDay();
    return d === 0 ? 6 : d - 1;
  });

  useEffect(() => {
    axios.get(`${API}/schedule`).then(r => setSchedule(r.data.schedule || [])).catch(() => {});
  }, []);

  const dayEntries = schedule.filter(e => e.day_of_week === selectedDay);
  dayEntries.sort((a, b) => a.start_time.localeCompare(b.start_time));

  const watNow = getWATDate();
  const currentTime = `${String(watNow.getHours()).padStart(2,'0')}:${String(watNow.getMinutes()).padStart(2,'0')}`;
  const isToday = (watNow.getDay() === 0 ? 6 : watNow.getDay() - 1) === selectedDay;

  return (
    <div data-testid="schedule-page" className="pb-28 min-h-screen">
      {/* Header */}
      <div className="relative py-20 sm:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1689290018811-ad3eef0c37f7?w=1600&q=80"
            alt="Gospel singers"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1115]/60 to-[#0F1115]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Programming</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Weekly Schedule
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Day Selector */}        <div className="flex gap-2 mb-10 overflow-x-auto pb-2">
          {DAYS.map((day, i) => {
            const isCurrent = i === selectedDay;
            const isTodayDay = (watNow.getDay() === 0 ? 6 : watNow.getDay() - 1) === i;
            return (
              <button
                key={day}
                data-testid={`day-selector-${i}`}
                onClick={() => setSelectedDay(i)}
                className={`px-5 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                  isCurrent
                    ? 'bg-[#D4AF37] text-[#0F1115]'
                    : 'bg-[#171A21] text-[#94A3B8] hover:bg-white/10 border border-white/5'
                }`}
              >
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{SHORT_DAYS[i]}</span>
                {isTodayDay && <span className="ml-1.5 text-xs opacity-70">(Today)</span>}
              </button>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="space-y-4">
          {dayEntries.length === 0 ? (
            <div className="text-center py-20">
              <CalendarIcon size={48} className="mx-auto text-[#94A3B8]/40 mb-4" />
              <p className="text-[#94A3B8] text-lg">No shows scheduled for {DAYS[selectedDay]}</p>
            </div>
          ) : (
            dayEntries.map((entry, i) => {
              const isLive = isToday && entry.start_time <= currentTime && entry.end_time > currentTime;
              return (
                <div
                  key={entry.id}
                  data-testid={`schedule-timeline-${i}`}
                  className={`flex gap-4 sm:gap-6 animate-fade-in-up ${i > 0 ? 'animation-delay-' + (i * 100) : ''}`}
                  style={{ animationDelay: `${i * 80}ms`, opacity: 0 }}
                >
                  {/* Time column */}
                  <div className="w-20 sm:w-24 shrink-0 text-right pt-4">
                    <p className="font-mono text-sm text-[#94A3B8]">{entry.start_time}</p>
                    <p className="font-mono text-xs text-[#94A3B8]/50">{entry.end_time}</p>
                  </div>
                  
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-3 h-3 rounded-full mt-5 ${isLive ? 'bg-red-500 live-pulse' : 'bg-[#D4AF37]'}`} />
                    {i < dayEntries.length - 1 && <div className="w-px flex-1 bg-white/10 mt-1" />}
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 p-5 rounded-2xl mb-2 ${
                    isLive
                      ? 'bg-[#171A21] border-l-4 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.15)]'
                      : 'bg-[#171A21] border border-white/5'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        {isLive && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-400 mb-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full live-pulse" /> ON AIR NOW
                          </span>
                        )}
                        <h3 className="text-white font-semibold text-lg">
                          {entry.show_id ? (
                            <Link
                              to={`/shows/${entry.show_id}`}
                              data-testid={`schedule-show-link-${i}`}
                              className="hover:text-[#D4AF37] transition-colors"
                            >
                              {entry.show?.name || 'TBA'}
                            </Link>
                          ) : (
                            entry.show?.name || 'TBA'
                          )}
                        </h3>
                        <p className="text-[#94A3B8] text-sm mt-1">{entry.show?.description}</p>
                        {entry.host && (
                          <div className="flex items-center gap-2 mt-3">
                            <img
                              src={entry.host.image_url || 'https://images.unsplash.com/photo-1581092800573-6afa755dcdc7?w=80'}
                              alt={entry.host.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <span className="text-[#D4AF37] text-sm font-medium">{entry.host.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <AdSlot slotKey="schedule" page="/schedule" />
      </div>
    </div>
  );
}
